import React, { useState } from "react";
import CameraView from "../../ScanningApp/components/CameraView";
import ScanOverlay from "../../ScanningApp/components/ScanOverlay";
import resStyles from "../css/scanningSessionResponsive.module.scss";

interface MobileStep2ScannerProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  torchOn: boolean;
  toggleTorch: () => void;
  vacutainerBarcode: string;
  vacutainerStatus: "idle" | "matched" | "mismatch";
  activeAliquotIndex: number;
  totalAliquots: number;
  aliquotBarcodes: string[];
  handleBarcodeSubmit: (barcode: string) => void;
  clearAndRestart: () => void;
  formMessage: { text: string; type: "success" | "error" | "" };
  completedSubs: number;
  totalSubs?: number;
  sessionData?: any;
  isProcessing?: boolean;
}

interface ScannerVideoBlockProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  torchOn: boolean;
  toggleTorch: () => void;
  formMessage: { text: string; type: "success" | "error" | "" };
}

const ScannerVideoBlock: React.FC<ScannerVideoBlockProps> = ({
  videoRef,
  torchOn,
  toggleTorch,
  formMessage,
}) => (
  <div className={resStyles.videoWrapper}>
    <CameraView videoRef={videoRef} />
    <ScanOverlay />
    {formMessage.text && (
      <div className={`${resStyles.scannerFeedbackOverlay} ${resStyles[formMessage.type]}`}>
        <i className={formMessage.type === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"}></i>
        <span>{formMessage.text}</span>
      </div>
    )}
    <button
      type="button"
      className={`${resStyles.torchBtn} ${torchOn ? resStyles.torchOn : ""}`}
      onClick={toggleTorch}
      aria-label={torchOn ? "Turn off torch" : "Turn on torch"}
    >
      <i className={torchOn ? "ri-flashlight-fill" : "ri-flashlight-line"}></i>
    </button>
  </div>
);

const MobileScanningStep2Scanner: React.FC<MobileStep2ScannerProps> = ({
  videoRef,
  torchOn,
  toggleTorch,
  vacutainerBarcode,
  vacutainerStatus,
  activeAliquotIndex,
  totalAliquots,
  aliquotBarcodes,
  handleBarcodeSubmit,
  clearAndRestart,
  formMessage,
  completedSubs,
  totalSubs,
  sessionData,
  isProcessing = false,
}) => {
  const [manualBarcode, setManualBarcode] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualBarcode.trim() || isProcessing) return;
    handleBarcodeSubmit(manualBarcode.trim());
    setManualBarcode("");
  };

  const handleClear = () => {
    if (isProcessing) return;
    setManualBarcode("");
    clearAndRestart();
  };

  const targetSubs = totalSubs || sessionData?.totalsubjects || sessionData?.total_subjects || 0;
  const currentCompletedSubs = completedSubs !== undefined ? completedSubs : (sessionData?.completed_aliquots ?? 0);

  const isStep2Completed =
    (targetSubs > 0 && currentCompletedSubs >= targetSubs && vacutainerStatus !== "matched") ||
    sessionData?.display_status === "COMPLETED" ||
    sessionData?.sessionstate === "completed";

  if (isStep2Completed) {
    return (
      <div className={`${resStyles.scanCard} ${resStyles.successCard}`} style={{ borderColor: "#16a34a", backgroundColor: "#f0fdf4", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px 20px", textAlign: "center" }}>
        <div className={resStyles.checkmarkWrapper} style={{ backgroundColor: "#16a34a", color: "#ffffff", width: "56px", height: "56px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px", marginBottom: "16px" }}>
          <i className="ri-checkbox-circle-fill"></i>
        </div>
        <h6 className="fw-bold text-success mb-2" style={{ fontSize: "18px" }}>Vacutainer to Aliquots Completed</h6>
        <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
          All {targetSubs} subjects and their aliquots have been successfully matched and scanned for this session.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* ── STEP 2: CARD 1: VACUTAINER ── */}
      {vacutainerStatus === "matched" ? (
        <div className={`${resStyles.scanCard} ${resStyles.successCard}`}>
          <div className={resStyles.checkmarkWrapper}>
            <i className="ri-checkbox-circle-fill"></i>
          </div>
          <div className={resStyles.successInfo}>
            <div className={resStyles.barcodeValue}>
              Barcode: {vacutainerBarcode}
            </div>
            <div className={resStyles.successLabel}>- VALID VACUTAINER</div>
          </div>
        </div>
      ) : (
        <div className={`${resStyles.scanCard} ${resStyles.activeCard}`}>
          <div className={resStyles.cardHeader}>
            <div
              className={`${resStyles.cardHeaderIcon} ${resStyles.vacutainerIcon}`}
            >
              <i className="ri-flask-line"></i>
            </div>
            <div className={resStyles.cardHeaderTitles}>
              <h6>SCAN VACUTAINER</h6>
              <p>Position the vacutainer barcode inside the camera view frame</p>
            </div>
          </div>
          <ScannerVideoBlock
            videoRef={videoRef}
            torchOn={torchOn}
            toggleTorch={toggleTorch}
            formMessage={formMessage}
          />

          <form className={resStyles.cardManualInputRow} onSubmit={handleSubmit}>
            <input
              type="text"
              aria-label="Vacutainer barcode"
              className={resStyles.cardManualInput}
              placeholder="Enter Vacutainer Barcode..."
              value={manualBarcode}
              onChange={(e) => setManualBarcode(e.target.value)}
              disabled={isProcessing}
            />
            <div className={resStyles.cardButtonRow}>
              <button
                type="submit"
                className={resStyles.cardOkBtn}
                style={{ minWidth: "48px" }}
                disabled={!manualBarcode.trim() || isProcessing}
              >
                {isProcessing ? "..." : "OK"}
              </button>
              <button
                type="button"
                className={resStyles.cardClearBtn}
                onClick={handleClear}
                disabled={isProcessing}
              >
                Clear
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── STEP 2: ALIQUOT CARDS DYNAMICALLY RENDERED ── */}
      {Array.from({ length: Math.max(0, Number(totalAliquots) || 0) }).map((_, index) => {
        const num = index + 1;
        const aliquotBarcode = aliquotBarcodes[index] || "";

        // If vacutainer is not matched, all aliquot cards are locked
        if (vacutainerStatus !== "matched") {
          return (
            <div
              key={index}
              className={`${resStyles.scanCard} ${resStyles.lockedCard}`}
            >
              <div className={resStyles.lockIconWrapper}>
                <i className="ri-lock-fill"></i>
              </div>
              <div className={resStyles.lockedLabel}>
                SCAN ALIQUOT {num} BARCODE
              </div>
              <div className={resStyles.lockedSublabel}>(WAITING)</div>
            </div>
          );
        }

        // If matched and this aliquot is already scanned
        if (num < activeAliquotIndex) {
          return (
            <div
              key={index}
              className={`${resStyles.scanCard} ${resStyles.successCard}`}
            >
              <div className={resStyles.checkmarkWrapper}>
                <i className="ri-checkbox-circle-fill"></i>
              </div>
              <div className={resStyles.successInfo}>
                <div className={resStyles.barcodeValue}>
                  Barcode: {aliquotBarcode}
                </div>
                <div className={resStyles.successLabel}>
                  - VALID ALIQUOT {num}
                </div>
              </div>
            </div>
          );
        }

        // If this is the current active aliquot card
        if (num === activeAliquotIndex) {
          return (
            <div
              key={index}
              className={`${resStyles.scanCard} ${resStyles.activeCard} ${resStyles.aliquotActive}`}
            >
              <div className={resStyles.cardHeader}>
                <div
                  className={`${resStyles.cardHeaderIcon} ${resStyles.aliquotIcon}`}
                >
                  <i className="ri-syringe-line"></i>
                </div>
                <div className={resStyles.cardHeaderTitles}>
                  <h6>
                    SCAN ALIQUOT {num} OF {totalAliquots}
                  </h6>
                  <p>
                    Position the aliquot barcode inside the camera view frame
                  </p>
                </div>
              </div>
              <ScannerVideoBlock
                videoRef={videoRef}
                torchOn={torchOn}
                toggleTorch={toggleTorch}
                formMessage={formMessage}
              />

              <form
                className={resStyles.cardManualInputRow}
                onSubmit={handleSubmit}
              >
                <input
                  type="text"
                  aria-label={`Aliquot ${num} barcode`}
                  className={resStyles.cardManualInput}
                  placeholder={`Enter Aliquot ${num} Barcode...`}
                  value={manualBarcode}
                  onChange={(e) => setManualBarcode(e.target.value)}
                  disabled={isProcessing}
                />
                <div className={resStyles.cardButtonRow}>
                  <button
                    type="submit"
                    className={`${resStyles.cardOkBtn} ${resStyles.aliquotBtn}`}
                    style={{ minWidth: "48px" }}
                    disabled={!manualBarcode.trim() || isProcessing}
                  >
                    {isProcessing ? "..." : "OK"}
                  </button>
                  <button
                    type="button"
                    className={resStyles.cardClearBtn}
                    onClick={handleClear}
                    disabled={isProcessing}
                  >
                    Clear
                  </button>
                </div>
              </form>
            </div>
          );
        }

        // If this is a future aliquot card (num > activeAliquotIndex)
        return (
          <div
            key={index}
            className={`${resStyles.scanCard} ${resStyles.lockedCard}`}
          >
            <div className={resStyles.lockIconWrapper}>
              <i className="ri-lock-fill"></i>
            </div>
            <div className={resStyles.lockedLabel}>
              SCAN ALIQUOT {num} BARCODE
            </div>
            <div className={resStyles.lockedSublabel}>(WAITING)</div>
          </div>
        );
      })}
    </>
  );
};

export default MobileScanningStep2Scanner;
